import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';

import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { CurrencyFootnote } from './currency-footnote.model';
import { TD_RATES_CURRENCIES } from './constants';

@Component({
  selector: 'eaa-currency-footnote-editor',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DropDownListModule, TextBoxModule, ButtonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyFootnoteEditorComponent),
      multi: true
    }
  ],
  template: `
    <div class="card" [formGroup]="form">
      <div class="card-header d-flex justify-content-between align-items-center">
        <div class="card-title m-0">Footnotes</div>
        <button
          type="button"
          class="btn btn-secondary border-0"
          (click)="addCurrencyFootnote()"
          [disabled]="form.disabled"
          title="Add footnote"
        >
          <i class="bi bi-plus"></i>
        </button>
      </div>

      <div class="card-body" formArrayName="currencyFootnotes">
        @for (footnoteGroup of currencyFootnotesArray.controls; track footnoteGroup; let i = $index) {
          <div [formGroupName]="i" class="card mb-2">
            <div class="card-body d-flex align-items-center gap-3">
              <div class="form-group">
                <ejs-dropdownlist
                  [id]="'currency_' + i"
                  formControlName="currency"
                  [dataSource]="currencies"
                  placeholder="Currency"
                  [allowFiltering]="true"
                  [fields]="{ text: 'value', value: 'value' }"
                  [popupHeight]="200"
                  (change)="onFormChange()"
                  floatLabelType="Auto"
                  [width]="120"
                />
              </div>
              <div class="form-group flex-fill">
                <ejs-textbox
                  [id]="'footnote_' + i"
                  formControlName="footnote"
                  placeholder="Footnote"
                  floatLabelType="Auto"
                  (input)="onFormChange()"
                />
              </div>
              <button
                type="button"
                class="btn btn-outline-danger border-0 mt-4"
                (click)="removeCurrencyFootnote(i)"
                [disabled]="form.disabled"
                title="Remove footnote"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  `
})
export class CurrencyFootnoteEditorComponent implements ControlValueAccessor {
  public form: FormGroup;
  public currencies = TD_RATES_CURRENCIES.map((currency) => ({ value: currency }));

  private onChange = (value: CurrencyFootnote[]) => {};
  private onTouched = () => {};

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      currencyFootnotes: this.fb.array([])
    });

    // Subscribe to form changes
    this.form.valueChanges.subscribe((value) => {
      const currencyFootnotes = value.currencyFootnotes || [];
      this.onChange(currencyFootnotes);
    });
  }

  get currencyFootnotesArray(): FormArray {
    return this.form.get('currencyFootnotes') as FormArray;
  }

  private createCurrencyFootnoteGroup(currencyFootnote?: CurrencyFootnote): FormGroup {
    return this.fb.group({
      currency: [currencyFootnote?.currency || '', Validators.required],
      footnote: [currencyFootnote?.footnote || '', Validators.required]
    });
  }

  addCurrencyFootnote(): void {
    const newGroup = this.createCurrencyFootnoteGroup();
    this.currencyFootnotesArray.push(newGroup);
    this.onTouched();
  }

  removeCurrencyFootnote(index: number): void {
    this.currencyFootnotesArray.removeAt(index);
    this.onTouched();
  }

  onFormChange(): void {
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: CurrencyFootnote[] | null): void {
    const currencyFootnotes = value || [];

    // Clear existing form array
    while (this.currencyFootnotesArray.length !== 0) {
      this.currencyFootnotesArray.removeAt(0);
    }

    // Add new form groups for each currency footnote
    currencyFootnotes.forEach((currencyFootnote) => {
      const group = this.createCurrencyFootnoteGroup(currencyFootnote);
      this.currencyFootnotesArray.push(group);
    });
  }

  registerOnChange(fn: (value: CurrencyFootnote[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
